import {
  Box,
  Divider,
  Grid,
  IconButton,
  Link,
  ListItemIcon,
  ListItemText,
  MenuItem,
  MenuList,
  Paper,
  Tooltip,
  Typography,
  Badge,
  AppBar,
  Container,
  Toolbar,
  Popper,
  Grow,
  ClickAwayListener,
  Avatar,
} from "@mui/material";
import PersonIcon from "@mui/icons-material/Person";
import HomeIcon from "@mui/icons-material/Home";
import LogoutIcon from "@mui/icons-material/Logout";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Menu from "@mui/icons-material/Menu";
import { logout } from "../../actions/auth";
import { useState, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { getPendingApplicationsCount } from "../../services/listingsServices/listing.service";
import SessionTimeoutModal from "../overlays/sessionTimeout";
import {isArray, isNullUndefined} from "../../util/util";

export default function ListingDashboardNavbar() {
  const [openSubMenu, setOpenSubMenu] = useState(false);
  const [mobileNav, setMobileNav] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);
  const isLoggedIn = useSelector((state) => state.auth.isLoggedIn);
  const user = useSelector((state) => state.auth.user);
  const anchorRef = useRef(null);
  const location = useLocation();
  const navigator = useNavigate();
  const dispatch = useDispatch();
  const [showManageProperty, setShowManageProperty] = useState(false);
  const propertyManagementRoles = ["ACC", "BLDIR", "BLDMG", "CS", "OWN", "REC", "SYSAD"]

  // Function to refresh pending count
  const refreshPendingCount = () => {
    getPendingApplicationsCount()
      .then((count) => {
        setPendingCount(count);
      })
      .catch((error) => {
        console.error("Error fetching pending count:", error);
        setPendingCount(0);
      });
  };

  // Fetch pending applications count
  useEffect(() => {
    refreshPendingCount();
  }, []);

  // Listen for application status changes
  useEffect(() => {
    const handleApplicationStatusChange = () => {
      refreshPendingCount();
    };

    // Add event listener for application status changes
    window.addEventListener(
      "applicationStatusChanged",
      handleApplicationStatusChange,
    );

    // Cleanup
    return () => {
      window.removeEventListener(
        "applicationStatusChanged",
        handleApplicationStatusChange,
      );
    };
  }, []);

  useEffect(() => {
    if (
      isLoggedIn &&
      user?.userId &&
      !isNullUndefined(user.roles) &&
      isArray(user.roles)
    ) {
      const hasMatch = user.roles.some(value => propertyManagementRoles.includes(value));
      setShowManageProperty(hasMatch);
    } else {
      setShowManageProperty(false);
    }
  }, [isLoggedIn, user]);

  function handleNavigate() {
    navigator("/listings/ManageListings");
  }

  function handleLogout() {
    if (isLoggedIn) {
      dispatch(logout());
    }
    navigator("/listings/signIn");
  }

  const navigateToProfile = () => {
    navigator("/listings/profile");
  };

  // Helper to check if a path is active
  const isActive = (path) => location.pathname === path;

  return (
    <AppBar position="static" elevation={5}>
      <Container maxWidth="xl">
        {isLoggedIn ? (
          <Toolbar
            component="div"
            sx={{
              display: "flex",
              flexDirection: "row",
              flexWrap: "nowrap",
              justifyContent: "space-between",
            }}
            disableGutters
          >
            <Link
              href="/listings"
              sx={{
                fontWeight: 700,
                textDecoration: "none",
              }}
            >
              <Tooltip title="Home">
                <Box
                  component="img"
                  src={require("../../images/kodi_icon.png")}
                  sx={{
                    objectFit: "contain",
                    objectPosition: "center center",
                    width: "50px",
                    height: "50px",
                    margin: "auto ",
                  }}
                  alt=""
                />
              </Tooltip>
            </Link>

            <Grid sx={{ display: { xs: "none", md: "flex" } }}>
              <Link
                href="/listings"
                sx={{
                  margin: "auto 5px",
                  padding: "10px",
                  borderRadius: "10px",
                  textDecoration: "none",
                  color: isActive("/listings") ? "#037171" : "#fff",
                  fontWeight: isActive("/listings") ? 900 : 700,
                  backgroundColor: isActive("/listings") ? "#fff" : "none",
                }}
              >
                Home
              </Link>
              <Link
                href="/listings/explore"
                sx={{
                  margin: "auto 5px",
                  padding: "10px",
                  borderRadius: "10px",
                  textDecoration: "none",
                  color: isActive("/listings/explore") ? "#037171" : "#fff",
                  fontWeight: isActive("/listings/explore") ? 900 : 700,
                  backgroundColor: isActive("/listings/explore")
                    ? "#fff"
                    : "none",
                }}
              >
                Explore
              </Link>
              <Link
                href="/listings/favorites"
                sx={{
                  margin: "auto 5px",
                  padding: "10px",
                  borderRadius: "10px",
                  textDecoration: "none",
                  color: isActive("/listings/favorites") ? "#037171" : "#fff",
                  fontWeight: isActive("/listings/favorites") ? 900 : 700,
                  backgroundColor: isActive("/listings/favorites")
                    ? "#fff"
                    : "none",
                }}
              >
                Favorites
              </Link>
              <Link
                href="/listings/appointments"
                sx={{
                  margin: "auto 5px",
                  padding: "10px",
                  borderRadius: "10px",
                  textDecoration: "none",
                  color: isActive("/listings/appointments")
                    ? "#037171"
                    : "#fff",
                  fontWeight: isActive("/listings/appointments") ? 900 : 700,
                  backgroundColor: isActive("/listings/appointments")
                    ? "#fff"
                    : "none",
                }}
              >
                Appointments
              </Link>
              <Badge
                badgeContent={pendingCount}
                color="error"
                sx={{
                  "& .MuiBadge-badge": {
                    fontSize: "0.75rem",
                    minWidth: "20px",
                    height: "20px",
                    borderRadius: "10px",
                  },
                }}
              >
                <Link
                  href="/listings/applications"
                  sx={{
                    margin: "auto 5px",
                    padding: "10px",
                    borderRadius: "10px",
                    textDecoration: "none",
                    color: isActive("/listings/applications")
                      ? "#037171"
                      : "#fff",
                    fontWeight: isActive("/listings/applications") ? 900 : 700,
                    backgroundColor: isActive("/listings/applications")
                      ? "#fff"
                      : "none",
                  }}
                >
                  Applications
                </Link>
              </Badge>

              { showManageProperty &&
                  <Link
                  href="/dashboard"
                  sx={{
                    margin: "auto 5px",
                    padding: "10px",
                    borderRadius: "10px",
                    textDecoration: "none",
                    color: isActive("/dashboard") ? "#037171" : "#fff",
                    fontWeight: isActive("/dashboard") ? 900 : 700,
                    backgroundColor: isActive("/dashboard")
                        ? "#fff"
                        : "none",
                  }}
              >
                Manage Property
              </Link>
              }

              <Tooltip title="Profile Options">
                <IconButton
                  sx={{
                    margin: "auto 20px",
                    color: "#037171",
                    fontWeight: 700,
                    textDecoration: "none",
                  }}
                  ref={anchorRef}
                  onClick={() => setOpenSubMenu(true)}
                >
                  <Avatar sx={{ backgroundColor: "#037171" }} />
                </IconButton>
              </Tooltip>
              <Popper
                open={openSubMenu}
                anchorEl={anchorRef.current}
                role={undefined}
                placement="bottom-start"
                transition
                disablePortal
                sx={{ zIndex: 1000 }}
              >
                {({ TransitionProps, placement }) => (
                  <Grow
                    {...TransitionProps}
                    style={{
                      transformOrigin:
                        placement === "bottom-start"
                          ? "center top"
                          : "center bottom",
                    }}
                  >
                    <Paper>
                      <ClickAwayListener
                        onClickAway={() => setOpenSubMenu(false)}
                      >
                        <MenuList
                          autoFocusItem={openSubMenu}
                          id="composition-menu"
                          aria-labelledby="composition-button"
                        >
                          <MenuItem
                            sx={{
                              color: isActive("/listings/ManageListings")
                                ? "#004d4d"
                                : "#037171",
                              fontWeight: isActive("/listings/ManageListings")
                                ? 900
                                : 600,
                              textAlign: "left",
                              width: "100%",
                              textDecoration: isActive(
                                "/listings/ManageListings",
                              )
                                ? "underline"
                                : "none",
                              padding: "8px 16px",
                            }}
                            onClick={handleNavigate}
                          >
                            <ListItemIcon>
                              <HomeIcon
                                fontSize="small"
                                sx={{ color: "#037171" }}
                              />
                            </ListItemIcon>
                            <ListItemText
                              sx={{
                                color: "#037171",
                                margin: 0,
                              }}
                            >
                              My Listings
                            </ListItemText>
                          </MenuItem>
                          <MenuItem
                            key="profile"
                            onClick={navigateToProfile}
                            sx={{
                              color: isActive("/listings/profile")
                                ? "#004d4d"
                                : "#037171",
                              fontWeight: isActive("/listings/profile")
                                ? 900
                                : 600,
                              textAlign: "left",
                              width: "100%",
                              textDecoration: isActive("/listings/profile")
                                ? "underline"
                                : "none",
                              padding: "8px 16px",
                            }}
                          >
                            <ListItemIcon>
                              <PersonIcon
                                fontSize="small"
                                sx={{ color: "#037171" }}
                              />
                            </ListItemIcon>
                            <ListItemText
                              sx={{
                                color: "#037171",
                                margin: 0,
                              }}
                            >
                              Profile
                            </ListItemText>
                          </MenuItem>
                          <MenuItem
                            sx={{
                              color: "red",
                              fontWeight: 600,
                              textAlign: "left",
                              width: "100%",
                              padding: "8px 16px",
                            }}
                            onClick={handleLogout}
                          >
                            <ListItemIcon>
                              <LogoutIcon
                                fontSize="small"
                                sx={{ color: "red" }}
                              />
                            </ListItemIcon>
                            <ListItemText
                              sx={{
                                color: "red",
                                margin: 0,
                              }}
                            >
                              Logout
                            </ListItemText>
                          </MenuItem>
                        </MenuList>
                      </ClickAwayListener>
                    </Paper>
                  </Grow>
                )}
              </Popper>
            </Grid>
            <Grid
              sx={{ display: { xs: "flex", md: "none", position: "relative" } }}
            >
              <IconButton
                sx={{ width: "100px" }}
                onClick={() => setMobileNav(!mobileNav)}
              >
                <Menu
                  sx={{
                    color: "#fff",
                    fontSize: "1.8rem",
                  }}
                />
              </IconButton>
            </Grid>
            <Paper
              sx={{
                width: "50%",
                margin: "0 0 0 45%",
                display: mobileNav ? "flex" : "none",
                textAlign: "start",
                position: "absolute",
                right: 0,
                top: "100%",
                zIndex: 1000,
              }}
            >
              <MenuList sx={{ width: "100%" }}>
                <MenuItem sx={{ width: "100%", textAlign: "start" }}>
                  <ListItemText sx={{ width: "100%" }}>
                    <Link
                      href="/listings/explore"
                      sx={{
                        width: "100%",
                        color: isActive("/listings/explore")
                          ? "#004d4d"
                          : "#037171",
                        fontWeight: isActive("/listings/explore") ? 900 : 700,
                        textAlign: "start",
                        textDecoration: isActive("/listings/explore")
                          ? "underline"
                          : "none",
                      }}
                    >
                      Explore
                    </Link>
                  </ListItemText>
                </MenuItem>
                <MenuItem sx={{ width: "100%" }}>
                  <ListItemText sx={{ width: "100%" }}>
                    <Link
                      href="/listings/favorites"
                      sx={{
                        width: "100%",
                        color: isActive("/listings/favorites")
                          ? "#004d4d"
                          : "#037171",
                        fontWeight: isActive("/listings/favorites") ? 900 : 700,
                        textAlign: "start",
                        textDecoration: isActive("/listings/favorites")
                          ? "underline"
                          : "none",
                      }}
                    >
                      Favourites
                    </Link>
                  </ListItemText>
                </MenuItem>
                <MenuItem sx={{ width: "100%" }}>
                  <ListItemText sx={{ width: "100%" }}>
                    <Link
                      href="/listings/appointments"
                      sx={{
                        width: "100%",
                        color: isActive("/listings/appointments")
                          ? "#004d4d"
                          : "#037171",
                        fontWeight: isActive("/listings/appointments")
                          ? 900
                          : 700,
                        textAlign: "start",
                        textDecoration: isActive("/listings/appointments")
                          ? "underline"
                          : "none",
                      }}
                    >
                      Appointments
                    </Link>
                  </ListItemText>
                </MenuItem>
                <MenuItem sx={{ width: "100%" }}>
                  <ListItemText sx={{ width: "100%" }}>
                    <Badge
                      badgeContent={pendingCount}
                      color="error"
                      sx={{
                        "& .MuiBadge-badge": {
                          fontSize: "0.75rem",
                          minWidth: "20px",
                          height: "20px",
                          borderRadius: "10px",
                        },
                      }}
                    >
                      <Link
                        href="/listings/applications"
                        sx={{
                          width: "100%",
                          color: isActive("/listings/applications")
                            ? "#004d4d"
                            : "#037171",
                          fontWeight: isActive("/listings/applications")
                            ? 900
                            : 700,
                          textAlign: "start",
                          textDecoration: isActive("/listings/applications")
                            ? "underline"
                            : "none",
                        }}
                      >
                        Applications
                      </Link>
                    </Badge>
                  </ListItemText>
                </MenuItem>
                { showManageProperty && (
                    <MenuItem sx={{ width: "100%" }}>
                      <ListItemText sx={{ width: "100%" }}>
                        <Badge
                            badgeContent={pendingCount}
                            color="error"
                            sx={{
                              "& .MuiBadge-badge": {
                                fontSize: "0.75rem",
                                minWidth: "20px",
                                height: "20px",
                                borderRadius: "10px",
                              },
                            }}
                        >
                          <Link
                              href="/dashboard"
                              sx={{
                                width: "100%",
                                color: isActive("/dashboard")
                                    ? "#004d4d"
                                    : "#037171",
                                fontWeight: isActive("/dashboard")
                                    ? 900
                                    : 700,
                                textAlign: "start",
                                textDecoration: isActive("/dashboard")
                                    ? "underline"
                                    : "none",
                              }}
                          >
                            Manage Property
                          </Link>
                        </Badge>
                      </ListItemText>
                    </MenuItem>
                )}
                <Divider sx={{ width: "75%", margin: "auto" }} />
                <MenuItem sx={{ width: "100%" }}>
                  <ListItemText sx={{ width: "100%" }}>
                    <Link
                      href="/listings/manageListings"
                      sx={{
                        width: "100%",
                        color: isActive("/listings/manageListings")
                          ? "#004d4d"
                          : "#037171",
                        fontWeight: isActive("/listings/manageListings")
                          ? 900
                          : 700,
                        textAlign: "start",
                        textDecoration: isActive("/listings/manageListings")
                          ? "underline"
                          : "none",
                      }}
                    >
                      Manage Listings
                    </Link>
                  </ListItemText>
                </MenuItem>
                <MenuItem sx={{ width: "100%" }}>
                  <ListItemText sx={{ width: "100%" }}>
                    <Typography
                      onClick={handleLogout}
                      sx={{
                        width: "100%",
                        color: "red",
                        fontWeight: 700,
                        textAlign: "start",
                      }}
                    >
                      Log Out
                    </Typography>
                  </ListItemText>
                </MenuItem>
              </MenuList>
            </Paper>
          </Toolbar>
        ) : (
          <Toolbar
            component="div"
            sx={{
              display: "flex",
              flexDirection: "row",
              flexWrap: "nowrap",
              justifyContent: "space-between",
            }}
            disableGutters
          >
            <Link
              href="/listings"
              sx={{
                margin: "auto 10px",
                color: "#fff",
                fontWeight: 700,
                textDecoration: "none",
              }}
            >
              <Box
                component="img"
                src={require("../../images/kodi_icon.png")}
                sx={{
                  objectFit: "contain",
                  objectPosition: "center center",
                  width: "50px",
                  height: "50px",
                  margin: "auto ",
                }}
                alt=""
              />
            </Link>

            <Box sx={{ display: { xs: "none", lg: "flex" } }}>
              <Link
                href="/listings"
                sx={{
                  margin: "auto 5px",
                  padding: "10px",
                  borderRadius: "10px",
                  textDecoration: "none",
                  color: isActive("/listings") ? "#037171" : "#fff",
                  fontWeight: isActive("/listings") ? 900 : 700,
                  backgroundColor: isActive("/listings") ? "#fff" : "none",
                }}
              >
                Home
              </Link>
              <Link
                href="/listings/explore"
                sx={{
                  margin: "auto 5px",
                  padding: "10px",
                  borderRadius: "10px",
                  textDecoration: "none",
                  color: isActive("/listings/explore") ? "#037171" : "#fff",
                  fontWeight: isActive("/listings/explore") ? 900 : 700,
                  backgroundColor: isActive("/listings/explore")
                    ? "#fff"
                    : "none",
                }}
              >
                Explore
              </Link>
              <Link
                href="/listings/register"
                sx={{
                  margin: "auto 5px",
                  padding: "10px",
                  borderRadius: "10px",
                  textDecoration: "none",
                  color: isActive("/listings/register") ? "#037171" : "#fff",
                  fontWeight: isActive("/listings/register") ? 900 : 700,
                  backgroundColor: isActive("/listings/register")
                    ? "#fff"
                    : "none",
                }}
              >
                Register
              </Link>
              <Link
                href="/listings/signIn"
                sx={{
                  margin: "auto 5px",
                  padding: "10px",
                  borderRadius: "10px",
                  textDecoration: "none",
                  color: isActive("/listings/signIn") ? "#037171" : "#fff",
                  fontWeight: isActive("/listings/signIn") ? 900 : 700,
                  backgroundColor: isActive("/listings/signIn")
                    ? "#fff"
                    : "none",
                }}
              >
                Sign in
              </Link>
            </Box>
            <Box sx={{ display: { xs: "flex", lg: "none" } }}>
              <IconButton
                sx={{ width: "100px" }}
                onClick={() => setMobileNav(!mobileNav)}
              >
                <Menu
                  sx={{
                    color: "#fff",
                    fontSize: "1.8rem",
                  }}
                />
              </IconButton>
            </Box>
            <Paper
              sx={{
                width: "50%",
                margin: "0 0 0 45%",
                display: mobileNav ? "flex" : "none",
                textAlign: "start",
                // position: "absolute",
                zIndex: 107,
              }}
            >
              <MenuList sx={{ width: "100%" }}>
                <MenuItem
                  sx={{
                    width: "100%",
                    textAlign: "start",
                    backgroundColor: isActive("/listings") ? "#037171" : "none",
                  }}
                >
                  <ListItemText sx={{ width: "100" }}>
                    <Link
                      href="/listings"
                      sx={{
                        width: "100%",
                        textAlign: "start",
                        textDecoration: "none",
                        color: isActive("/listings") ? "#037171" : "#fff",
                        fontWeight: isActive("/listings") ? 900 : 700,
                      }}
                    >
                      Home
                    </Link>
                  </ListItemText>
                </MenuItem>
                <MenuItem
                  sx={{
                    width: "100%",
                    textAlign: "start",
                    backgroundColor: isActive("/listings/explore")
                      ? "#037171"
                      : "none",
                  }}
                >
                  <ListItemText sx={{ width: "100%" }}>
                    <Link
                      href="/listings/explore"
                      sx={{
                        width: "100%",
                        textAlign: "start",
                        textDecoration: "none",
                        color: isActive("/listings/explore")
                          ? "#037171"
                          : "#fff",
                        fontWeight: isActive("/listings/explore") ? 900 : 700,
                      }}
                    >
                      Explore
                    </Link>
                  </ListItemText>
                </MenuItem>
                <MenuItem
                  sx={{
                    width: "100%",
                    textAlign: "start",
                    backgroundColor: isActive("/listings/register")
                      ? "#037171"
                      : "none",
                  }}
                >
                  <ListItemText sx={{ width: "100%" }}>
                    <Link
                      href="/listings/register"
                      sx={{
                        width: "100%",
                        textAlign: "start",
                        textDecoration: "none",
                        color: isActive("/listings/register")
                          ? "#037171"
                          : "#fff",
                        fontWeight: isActive("/listings/register") ? 900 : 700,
                      }}
                    >
                      Register
                    </Link>
                  </ListItemText>
                </MenuItem>
                <MenuItem
                  sx={{
                    width: "100%",
                    textAlign: "start",
                    backgroundColor: isActive("/listings/signIn")
                      ? "#037171"
                      : "none",
                  }}
                >
                  <ListItemText sx={{ width: "100%" }}>
                    <Link
                      href="/listings/signIn"
                      sx={{
                        width: "100%",
                        textAlign: "start",
                        textDecoration: "none",
                        color: isActive("/listings/signIn")
                          ? "#037171"
                          : "#fff",
                        fontWeight: isActive("/listings/signIn") ? 900 : 700,
                      }}
                    >
                      Sign In
                    </Link>
                  </ListItemText>
                </MenuItem>
              </MenuList>
            </Paper>
          </Toolbar>
        )}
      </Container>
      <Box sx={{ position: "relative", width: "100%", height: "0" }}>
        <SessionTimeoutModal />
      </Box>
    </AppBar>
  );
}
